import APITemplate from '@api/APITemplate'
import { diffTimeMinutes, formatQueryParams } from '@utils/Format'
import store from '@state/store'
import { MINUTES_TIMEOUT_REQUESTS } from '@constants/Numbers'
import con from '@services/connection'
import { docs, external, pjsEdit } from '@modules/documents/constants/URL'
import moment from 'moment'
import { MODAL } from '@constants/Text'
import { GlobalBus } from '@services/bus'
import { apiDocumentsSigners } from '@modules/documents/api/apiDocumentsSigners'
import { sortByDate } from '@utils/Sort'
import { downloadBase64 } from '@utils/Download'
import { deriveContractStatus } from '@modules/documents/utils/derive-contract-status'

const TIMER_TO_RELOAD = 1500

class APIDocuments extends APITemplate {
  formatValues(val) {
    let copy = JSON.parse(JSON.stringify(val))
    if (!Array.isArray(copy)) {
      copy = [copy]
    }

    const list = copy.map((doc) => {
      const config = doc.config || {}
      return {
        id: doc.id,
        id_template: doc.id_template,
        id_tipo: doc.id_tipo,
        variaveis: doc.variaveis,
        nome_documento: doc.nome_documento,
        status: doc.status,
        motivo: doc.motivo,
        observacoes: doc.observacoes,
        data_registro: doc.data_registro,
        data_atualizacao: doc.data_atualizacao,
        data_fim_vigencia: doc.data_fim_vigencia,
        data_inicio_vigencia: doc.data_inicio_vigencia,
        provider_id: doc.provider_id,
        deadline_at: config.deadline_at
          ? moment(config.deadline_at).format('DD/MM/YYYY')
          : '',
        remind_interval: config.remind_interval,
        block_after_refusal: config.block_after_refusal,
        signers: config.signers || [],
        external_signature: doc.external_signature,
        id_app_clientes: doc.id_app_clientes,
        _signatures: doc._signatures || [],
        tipo: doc.tipo,
        id_pjs: doc.pjs,
        _status: deriveContractStatus(doc),
      }
    })

    return sortByDate({ items: list, key: 'data_registro' })
  }

  async carregar(
    { professionals, professional, id_equipe, only_client } = {},
    forceUpdate = false,
    save = true
  ) {
    const cached = this._resolveCachedDocuments({ save, forceUpdate })
    if (cached !== null) return cached

    if (save) {
      store.dispatch('documents/setDocumentsLoading', true)
    }

    const params = this._buildCarregarParams({
      professionals,
      professional,
      id_equipe,
      only_client,
    })
    const r = await con.get(docs.documents + formatQueryParams(params))
    const result = this._applyCarregarResponse({ r, save })

    if (save) {
      store.dispatch('documents/setDocumentsLoading', false)
    }

    return result
  }

  _resolveCachedDocuments({ save, forceUpdate }) {
    if (!save) return null
    const isFresh =
      !forceUpdate &&
      !diffTimeMinutes(
        store.getters['documents/documentsLastUpdate'],
        MINUTES_TIMEOUT_REQUESTS
      )
    if (isFresh) return store.getters['documents/documents']
    // forceUpdate=true precisa de dado novo, mesmo com outra carga em voo:
    // devolver o cache aqui fazia quem deu `await` seguir com o documento
    // ANTERIOR (a request em voo ainda não voltou) e renderizar signatários
    // de outro documento. Reproduzido ao abrir um contrato recém-criado
    // enquanto a listagem ainda carregava.
    if (!forceUpdate && store.getters['documents/documentsLoading']) {
      return store.getters['documents/documents']
    }
    return null
  }

  _buildCarregarParams({
    professionals,
    professional,
    id_equipe,
    only_client,
  }) {
    const params = { with_signatures: true, only_client }
    let resolvedProfessionals = professionals
    const hasProfessionals = !!(
      resolvedProfessionals && resolvedProfessionals.length
    )
    if (professional && professional !== -1 && !hasProfessionals) {
      resolvedProfessionals = [professional]
    }
    if (resolvedProfessionals && resolvedProfessionals.length) {
      params.professional_ids = resolvedProfessionals.join(',')
    }
    if (id_equipe) {
      params.filters_team = JSON.stringify({ id_equipe })
    }
    return params
  }

  _applyCarregarResponse({ r, save }) {
    const ok = r && r.status === 200
    const formattedValue = ok ? this.formatValues(r.value) : null
    if (save) {
      store.dispatch('documents/setDocuments', ok ? formattedValue || [] : [])
    }
    if (ok) return { ...r, value: formattedValue }
    return r || {}
  }

  _resolveCachedDocumentsByClient({ id_cliente_app, forceUpdate, save }) {
    if (!save || forceUpdate) return null
    const cachedList =
      store.getters['documents/documentsByClient'](id_cliente_app)
    const lastUpdate =
      store.getters['documents/documentsByClientLastUpdate'](id_cliente_app)
    const isFresh =
      cachedList &&
      cachedList.length !== undefined &&
      lastUpdate &&
      !diffTimeMinutes(lastUpdate, MINUTES_TIMEOUT_REQUESTS)
    if (isFresh) {
      return { status: 200, message: 'Cache válido.', value: cachedList }
    }
    const isLoading =
      store.getters['documents/documentsByClientLoading'](id_cliente_app)
    if (isLoading) {
      return {
        status: 200,
        message: 'Carregando.',
        value: cachedList || [],
      }
    }
    return null
  }

  _applyCarregarPorClienteResponse({ r, id_cliente_app, save }) {
    const ok = r && r.status === 200
    const formattedValue = ok ? this.formatValues(r.value) : null
    if (save && ok) {
      store.dispatch('documents/setDocumentsByClient', {
        id_cliente: id_cliente_app,
        value: formattedValue || [],
      })
    }
    if (ok) return { ...r, value: formattedValue }
    return r || {}
  }

  async carregarPorCliente({
    id_cliente_app,
    forceUpdate = false,
    save = true,
  } = {}) {
    if (id_cliente_app === undefined || id_cliente_app === null) {
      return {
        status: 404,
        message: 'Cliente não identificado.',
        value: [],
      }
    }

    const cached = this._resolveCachedDocumentsByClient({
      id_cliente_app,
      forceUpdate,
      save,
    })
    if (cached !== null) return cached

    if (save) {
      store.dispatch('documents/setDocumentsByClientLoading', {
        id_cliente: id_cliente_app,
        loading: true,
      })
    }

    const params = {
      with_signatures: true,
      only_client: true,
      filters: JSON.stringify({ id_app_clientes: id_cliente_app }),
    }
    const r = await con.get(docs.documents + formatQueryParams(params))
    const result = this._applyCarregarPorClienteResponse({
      r,
      id_cliente_app,
      save,
    })

    if (save) {
      store.dispatch('documents/setDocumentsByClientLoading', {
        id_cliente: id_cliente_app,
        loading: false,
      })
    }

    return result
  }

  async salvar({ id, data, config, variaveis, toEdit }) {
    if (config.deadline_at) {
      if (moment(config.deadline_at, 'DD/MM/YYYY').isValid()) {
        config.deadline_at = moment(config.deadline_at, 'DD/MM/YYYY').format(
          'YYYY-MM-DD'
        )
      }
    }

    const params = {
      config: JSON.stringify(config),
    }

    let r
    let resEdit

    if (id) {
      params.data = JSON.stringify({
        data_inicio_vigencia: data.data_inicio_vigencia,
        data_fim_vigencia: data.data_fim_vigencia,
        id_tipo: data.id_tipo,
        is_client_contract: data.is_client_contract,
      })

      r = await con.put(docs.documents + `/${id}`, params)

      if (r && r.status === 200 && toEdit && toEdit.length) {
        resEdit = await this.modeEditPjDocument({
          listData: toEdit,
          id_document: id,
        })
      }

      if (resEdit && resEdit.status !== 200) {
        r.message =
          'Salvo com sucesso, mas houve erro ao salvar os dados de edição das PJ(s).'
      }
    } else {
      params.data = JSON.stringify(data)
      params.variaveis = JSON.stringify(variaveis) // { nome_variavel: valor } ou [{nome_variavel, valor, id_variavel_sistema, id_entidade}]
      r = await con.post(docs.documents, params)
    }
    // r = mocks

    if (r && r.status === 200) {
      const item = this.formatValues(r.value[0] || r.value || {})
      GlobalBus.$emit(MODAL.DOCS_RELOAD_DOCUMENT, { item })
      store.dispatch('documents/addDocuments', item)
    }

    return r || {}
  }

  async salvarPDF({ id, data, config, file, toEdit }) {
    if (config.deadline_at) {
      if (moment(config.deadline_at, 'DD/MM/YYYY').isValid()) {
        config.deadline_at = moment(config.deadline_at, 'DD/MM/YYYY').format(
          'YYYY-MM-DD'
        )
      }
    }

    if (data && data.nome_documento) {
      if (!data.nome_documento.endsWith('.')) {
        const extension = file.name.split('.').pop()
        data.nome_documento += `.${extension}`
      }
    }

    const params = {
      config: JSON.stringify(config),
    }

    let r
    let resEdit

    if (id) {
      params.data = JSON.stringify({
        data_inicio_vigencia: data.data_inicio_vigencia,
        data_fim_vigencia: data.data_fim_vigencia,
        id_tipo: data.id_tipo,
      })
      r = await con.put(docs.documents + `/${id}`, params)

      if (r && r.status === 200 && toEdit && toEdit.length) {
        resEdit = await this.modeEditPjDocument({
          listData: toEdit,
          id_document: id,
        })
      }

      if (resEdit && resEdit.status !== 200) {
        r.message =
          'Salvo com sucesso, mas houve erro ao salvar os dados de edição das PJ(s).'
      }
    } else {
      params.data = JSON.stringify(data)
      params.file = file

      r = await con.post(docs.pdf, params)
    }

    if (r && r.status === 200) {
      const item = this.formatValues(r.value[0] || r.value || {})

      GlobalBus.$emit(MODAL.DOCS_RELOAD_DOCUMENT, { item })
      store.dispatch('documents/addDocuments', item)
    }

    return r || {}
  }

  async importExternalContract({ file, dados }) {
    const data = {
      data: JSON.stringify(dados),
      file: file,
    }

    const r = await con.post(external.import, data)

    if (r && r.status === 200) {
      // Aqui, espera-se que seja a mesma resposta da importação de PDF.
      const item = this.formatValues(r.value[0] || r.value || {})
      GlobalBus.$emit(MODAL.DOCS_RELOAD_DOCUMENT, { item })
      store.dispatch('documents/addDocuments', item)
    }

    return r || {}
  }

  /** Campos que descrevem a ASSINATURA (iguais nos três tipos de signatário). */
  _dadosDaAssinatura(signature) {
    return {
      id: signature.id,
      id_documento: signature.id_documento,
      id_signatario: signature.id_signatario,
      assinando_como: signature.assinando_como,
      status: signature.status,
      motivo: signature.motivo,
      observacores: signature.observacores,
      auth: signature.auth,
    }
  }

  _montarSignatarioProfissional({ signature, signers, professionals }) {
    const signer =
      signers.find(
        (item) => item.id_profissional === signature.id_profissional
      ) || {}
    const professional =
      professionals.find(
        (item) => item.id_profissional === signer.id_profissional
      ) || {}

    return {
      ...this._dadosDaAssinatura(signature),
      // Dados do signatario
      id_profissional: signer.id_profissional || signature.id_profissional,
      email: signer.email || signature.email,
      cpf: signer.cpf || signature.cpf,
      ativo: signer.ativo || signature.ativo,
      // Dados do profissional
      nome: professional.nome || signature.nome,
    }
  }

  /**
   * Cliente cujo cadastro corresponde ao signatário desta assinatura.
   *
   * Signatário sem vínculo de cliente (ex.: o signatário obrigatório da AAWZ)
   * não casa com cliente nenhum: sem o guard, os dois lados ficam undefined e
   * `undefined === undefined` fazia o find retornar o PRIMEIRO cliente da
   * lista — o institucional aparecia com o nome/e-mail desse cliente aleatório.
   *
   * `signer.id` (UUID do signatário) NÃO entra na comparação: casar UUID de
   * signatário com id de cliente nunca é match legítimo.
   */
  _encontrarClienteDoSignatario({ signer, signature, clients }) {
    const signerId = signer.id_app_clientes || signer.id_cliente || null
    const signatureId =
      signature.id_app_clientes || signature.id_cliente || null

    if (!signerId && !signatureId) return {}

    const encontrado = clients.find((item) => {
      const identifier =
        item.id_app_clientes || item.id_cliente || item.id || null
      if (signerId && identifier === signerId) return true
      if (!signatureId) return false
      return identifier === signatureId || item.id_cliente_web === signatureId
    })

    return encontrado || {}
  }

  _montarSignatarioAgenteExterno({ signature, agent }) {
    return {
      ...this._dadosDaAssinatura(signature),
      id_agente_externo: agent.id || signature.id,
      email: agent.email || signature.email,
      cpf: agent.cpf || agent.documento || signature.cpf,
      ativo: agent.ativo || signature.ativo,
      nome: agent.nome || signature.nome,
    }
  }

  /**
   * Primeiro valor preenchido da lista, com o último como fallback —
   * equivalente a uma cadeia de `||`, sem o custo de complexidade ciclomática
   * que ela cobra quando são 8 candidatos (regra `complexity` do ESLint).
   */
  _primeiroPreenchido(valores) {
    // `find(Boolean)`: Boolean ignora os argumentos extras (index, array) que o
    // find passa, então é equivalente ao arrow — e é o que o Sonar pede.
    const encontrado = valores.find(Boolean)
    return encontrado === undefined ? valores[valores.length - 1] : encontrado
  }

  _montarSignatarioCliente({ signature, signer, clientData }) {
    return {
      ...this._dadosDaAssinatura(signature),
      id_app_clientes: this._primeiroPreenchido([
        signer.id_app_clientes,
        signer.id_cliente,
        signature.id_app_clientes,
        signature.id_cliente,
        clientData.id_app_clientes,
        clientData.id_cliente,
        clientData.id,
        null,
      ]),
      // O backend já resolve nome/e-mail do signatário DESTE documento
      // (coalesce profissional/agente/cliente + signatário institucional).
      // Por isso `signature.*` vem primeiro: o cadastro do cliente
      // (clientData) é fallback, não fonte — senão o e-mail cadastral
      // sobrescreve o e-mail com que a pessoa realmente assina.
      email: this._primeiroPreenchido([
        signature.email,
        signer.email,
        clientData.email,
        clientData.login,
      ]),
      cpf: this._primeiroPreenchido([
        signature.cpf,
        signer.cpf,
        clientData.cpf,
        clientData.documento,
      ]),
      ativo: this._primeiroPreenchido([
        clientData.ativo,
        signer.ativo,
        signature.ativo,
      ]),
      nome: this._primeiroPreenchido([
        signature.nome,
        clientData.nome_cliente,
        clientData.name,
      ]),
    }
  }

  async carregarSignatarios({ id_documento }) {
    if (!id_documento) {
      return {
        status: 404,
        message: 'Documento não encontrado',
        value: {},
      }
    }
    const signersData = await apiDocumentsSigners.carregar()

    const signers = signersData.value || signersData || []
    const professionals = store.getters['bi/profissionais']
    const externalAgents = store.getters['bi/externAgents']
    const clients = store.getters['user/allClients'] || []

    const r = await con.get(docs.documentSigners(id_documento))

    const aux = {
      _signers: [],
      _external_signers: [],
      _clients_signers: [],
    }
    if (!r || r.status !== 200) return aux

    r.value._signers.forEach((signature) => {
      aux._signers.push(
        this._montarSignatarioProfissional({
          signature,
          signers,
          professionals,
        })
      )
    })

    r.value._external_signers.forEach((signature) => {
      const signer =
        signers.find((item) => item.id === signature.id_signatario) || {}
      const agent =
        externalAgents.find((item) => item.id === signer.id_agente_externo) ||
        {}

      if (signature.tipo_signatario === 2) {
        aux._external_signers.push(
          this._montarSignatarioAgenteExterno({ signature, agent })
        )
        return
      }

      const clientData = this._encontrarClienteDoSignatario({
        signer,
        signature,
        clients,
      })
      aux._clients_signers.push(
        this._montarSignatarioCliente({ signature, signer, clientData })
      )
    })

    return aux
  }

  async externalContractAddSigners({ id_documento, signers, oldSigners }) {
    if (!signers.length) {
      return {
        status: 200,
        message: 'Nenhum signatário para ser cadastrado',
        value: [],
      }
    }
    if (!id_documento) {
      return {
        status: 404,
        message: 'Documento não encontrado',
        value: {},
      }
    }

    const signersToSave = signers.filter(
      (item) =>
        !!item.id_profissional ||
        !!item.id_agente_externo ||
        !!item.id_app_clientes
    )
    const data = {
      data: JSON.stringify(
        signersToSave.map((item) => {
          return {
            id_profissional: item.id_profissional,
            id_agente_externo: item.id_agente_externo,
            id_app_clientes: item.id_app_clientes,
            sign_as: item.assinando_como || item.roles[0],
            auth: item.auth,
          }
        })
      ),
    }
    const r = await con.post(`${docs.signer}/${id_documento}/bulk`, data)

    if (r.status === 200) {
      GlobalBus.$emit(MODAL.DOCS_RELOAD_DOC_SIGNERS)
    }

    return r || {}
  }

  async adicionarSignatarios({ id_documento, signers, oldSigners }) {
    if (!signers.length) {
      return {
        status: 200,
        message: 'Nenhum signatário para ser cadastrado',
        value: [],
      }
    }

    let existentes = oldSigners || [] // (await apiDocumentsSigners.carregar())
    existentes = existentes.value || existentes || []
    // signer: {id_profissional, email, roles}
    const res = await Promise.all(
      signers.map(async (signer) => {
        if (!signer.id_signatario) {
          const old = existentes.find((item) => {
            const sameProfessional =
              item.id_profissional === signer.id_profissional
            const sameExternal =
              item.id_agente_externo === signer.id_agente_externo
            const sameClient =
              item.id_app_clientes === signer.id_app_clientes ||
              item.id_cliente === signer.id_app_clientes

            return (
              (sameProfessional || sameExternal || sameClient) &&
              item.email === signer.email &&
              item.cpf === signer.cpf
            )
          })
          if (old) {
            signer.id_signatario = old.id_signatario
          } else {
            const addSigner = await apiDocumentsSigners.salvar({
              email: signer.email,
              id_profissional: signer.id_profissional,
              id_agente_externo: signer.id_agente_externo,
              id_app_clientes: signer.id_app_clientes,
              cpf: signer.cpf,
            })
            if (addSigner.status === 200) {
              const item = addSigner.value[0] || addSigner.value || {}
              signer.id_signatario = item.id
            } else {
              return addSigner
            }
          }
        }

        const isRegistred = existentes.find(
          (item) =>
            item.id_signatario === signer.id_signatario &&
            item.email === signer.email &&
            item.cpf === signer.cpf &&
            item.assinando_como === signer.assinando_como
        )
        let r
        if (isRegistred) {
          r = {
            status: 200,
            message: 'Signatário registrado com sucesso',
            value: { ...signer },
          }
        } else {
          const payload = {
            id_signatario: signer.id_signatario,
            sign_as: signer.assinando_como || signer.roles[0],
          }

          if (signer.id_app_clientes || signer.id_cliente) {
            payload.id_cliente = signer.id_app_clientes || signer.id_cliente
          }

          const data = {
            data: JSON.stringify(payload),
          }
          r = await con.post(docs.documentSigners(id_documento), data)
        }

        return r || {}
      })
    )

    const erro = res.find((item) => {
      return item.status !== 200
    })

    // Timeout pois o back nao respondia a tempo com a atualizacao
    setTimeout(() => {
      GlobalBus.$emit(MODAL.DOCS_RELOAD_DOC_SIGNERS)
    }, TIMER_TO_RELOAD)

    if (erro) {
      return erro
    } else {
      return res[0]
    }
  }

  async removerSignatario({ id_documento, id_signer }) {
    const r = await con.delet(
      docs.documentSigners(id_documento) + `/${id_signer}`
    )
    return r || {}
  }
  async removerSignatarios({ id_documento, signers }) {
    signers = signers.filter((signer) => signer.id)
    if (!signers.length) {
      return {
        status: 200,
        message: 'Nenhum signatário para ser removido',
        value: [],
      }
    }
    const reqs = await Promise.all(
      signers.map((signer) => {
        return this.removerSignatario({ id_documento, id_signer: signer.id })
      })
    )
    // Para garantir que o servidor vai responder com dado atualizado
    setTimeout(() => {
      GlobalBus.$emit(MODAL.DOCS_RELOAD_DOC_SIGNERS)
    }, TIMER_TO_RELOAD)
    const erro = reqs.find((r) => r.status !== 200)
    if (erro) {
      return erro
    } else {
      return reqs[0] || {}
    }
  }

  /**
   * Notificar todos os signatários do documento.
   * @param id_documento
   * @returns {Promise<*|{}>}
   */
  async notificarSignatarios({ id_documento, mensagem }) {
    if (!id_documento) {
      return {
        status: 404,
        message: 'Documento não encontrado',
        value: {},
      }
    }
    const data = {
      data: JSON.stringify({ message: mensagem }),
    }

    const r = await con.put(docs.documentSignersNotify(id_documento), data)
    return r || {}
  }

  /**
   * Notificar apenas um signatário especificado.
   * @param id_document
   * @param id_signature
   * @returns {Promise<*|{}>}
   */
  async notificarSignatario({ id_documento, id_signature, mensagem }) {
    if (!id_documento) {
      return {
        status: 404,
        message: 'Documento não encontrado',
        value: {},
      }
    }
    if (!id_signature) {
      return {
        status: 404,
        message: 'Signatário não encontrado',
      }
    }

    const data = {
      data: JSON.stringify({ message: mensagem }),
    }

    const r = await con.put(
      docs.documentSigners(id_documento) + `/${id_signature}/notify`,
      data
    )
    return r || {}
  }

  async cancelDocument({ id, only_client }) {
    if (!id) {
      return {
        status: 404,
        message: 'Documento não encontrado',
        value: null,
      }
    }

    const r = await con.post(docs.documents + `/${id}/cancel`)

    if (r && r.status === 200) {
      this.carregar({ only_client }, true)
    }

    return r || {}
  }

  async removeDocument({ id, only_client }) {
    if (!id) {
      return {
        status: 404,
        message: 'Documento não encontrado',
        value: null,
      }
    }
    const r = await con.delet(docs.documents + `/${id}`)

    if (r && r.status === 200) {
      this.carregar({ only_client }, true)
    }

    return r || {}
  }

  async downloadDocument({ id }, breakDownload) {
    if (!id) {
      return {
        status: 404,
        message: 'Documento não encontrado',
        value: {},
      }
    }

    const r = await con.get(docs.documents + `/${id}/export`)

    if (r.status === 200 && r.value && !breakDownload) {
      const { base64, name, extension } = r.value
      downloadBase64(base64, name, extension)
    }

    return r || {}
  }

  async sendNotification({ recipients, subject }) {
    if (!recipients.length) {
      return {
        status: 404,
        message: 'Nenhum destinatário informado.',
        value: {},
      }
    }

    const data = {
      recipients: JSON.stringify(recipients),
      subject,
    }

    const r = await con.put(docs.notify, data)

    return r || {}
  }

  async modeEditPjDocument({ listData, id_document }) {
    const errors = []
    await Promise.all(
      listData.map(async (item) => {
        if (item.id) {
          const r = await con.delet(pjsEdit.addDelete(item.id))
          if (r.status !== 200) {
            errors.push(r)
          }
        } else {
          const data = {
            data: JSON.stringify({
              id_pj: item,
              id_documento: id_document,
            }),
          }
          const r = await con.post(pjsEdit.addDelete(), data)

          if (r.status !== 200) {
            errors.push(r)
          }
        }
      })
    )

    if (errors.length) {
      return errors[0]
    }

    return { status: 200, message: 'Salvo com sucesso.' }
  }
}

const apiDocuments = new APIDocuments()
export { apiDocuments }
